import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Col, CustomInput, Input, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import { AddRoomAPI, DeleteRoomAPI, GetRoomsAPI, GetShiftsAPI, SaveLayoutAPI, SaveRoomAPI } from "network/bookings";
import { errText } from "./errText";
import PlanCanvas from "./PlanCanvas";
import {
  ROTATIONS,
  clampPos,
  coversByKind,
  dragTo,
  duplicateTable,
  nameError,
  newTable,
  nudge,
  numberPatch,
  roomSummary,
  shapePatch,
} from "./planLogic";

const GOLD = "#b8932f";
const RED = "#c62828";
const SHAPE_ICON = { square: "■", rect: "▬", round: "●" };

let seq = 0;
const withKey = (t) => ({ ...t, key: t.id || `n${++seq}` });
const strip = ({ key, ...rest }) => rest;

// Campo numerico: in digitazione applica solo valori validi, uscendo (Invio o clic fuori) corregge il valore.
const NumField = ({ label, value, field, tb, onPatch, min, max, step = 1 }) => {
  const [draft, setDraft] = useState(null);
  const commit = (raw) => {
    const p = numberPatch(tb, field, raw, true);
    if (p) onPatch(p);
    setDraft(null);
  };
  return (
    <>
      <label className="mb-0 small">{label}</label>
      <Input
        type="number"
        bsSize="sm"
        min={min}
        max={max}
        step={step}
        value={draft ?? value}
        onFocus={() => setDraft(String(value))}
        onChange={(e) => {
          setDraft(e.target.value);
          const p = numberPatch(tb, field, e.target.value, false);
          if (p) onPatch(p);
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      />
    </>
  );
};

// Sala e tavoli: sale del locale, pianta con i tavoli trascinabili, proprietà di ogni tavolo.
const FloorEditor = () => {
  const { t } = useTranslation("bookings");
  const [rooms, setRooms] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [tables, setTables] = useState([]);
  const [sel, setSel] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [roomForm, setRoomForm] = useState(null); // {mode: "new"|"rename", name}
  const [askDelete, setAskDelete] = useState(false);
  const [pending, setPending] = useState(null); // azione in attesa di conferma (modifiche non salvate)
  const [nameDraft, setNameDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const root = useRef(null);
  const room = rooms.find((r) => r.id === roomId);

  const load = useCallback(async (keep) => {
    try {
      const data = await GetRoomsAPI();
      setRooms(data);
      const cur = data.find((r) => r.id === keep) || data[0];
      setRoomId(cur ? cur.id : null);
      setTables(cur ? cur.tables.map(withKey) : []);
      setSel(null);
      setDirty(false);
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    load();
    GetShiftsAPI().then((s) => setShifts(Array.isArray(s) ? s : s?.results || [])).catch(() => setShifts([]));
  }, [load]);

  // Uscita dalla pagina con modifiche non salvate: chiusura/ricarica e link interni chiedono conferma
  useEffect(() => {
    if (!dirty) return undefined;
    const msg = t("plan.leaveConfirm");
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = msg;
      return msg;
    };
    const click = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || (root.current && root.current.contains(a)) || a.target === "_blank" || e.defaultPrevented) return;
      if (!window.confirm(msg)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", click, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", click, true);
    };
  }, [dirty, t]);

  // Nomi dei tavoli delle altre sale (unici in tutte le sale)
  const otherRoomNames = useMemo(
    () => rooms.filter((r) => r.id !== roomId).flatMap((r) => r.tables.map((x) => x.name)),
    [rooms, roomId]
  );
  const allNames = [...otherRoomNames, ...tables.map((x) => x.name)];
  const tb = tables.find((x) => x.key === sel);

  const update = (fn) => {
    setTables(fn);
    setDirty(true);
  };
  const patch = (key, p) =>
    update((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));
  // patch che tiene il tavolo dentro la pianta (misure, rotazione)
  const patchFit = (key, p) =>
    update((list) =>
      list.map((x) => {
        if (x.key !== key) return x;
        const next = { ...x, ...p };
        return { ...next, ...clampPos(next, next.x, next.y, room) };
      })
    );
  const select = (key) => {
    setSel(key);
    setNameDraft(null);
  };

  // Frecce della tastiera: 10 px, con Maiusc 1 px (non quando si scrive in un campo)
  useEffect(() => {
    if (!tb) return undefined;
    const onKey = (e) => {
      const el = e.target;
      if (el && (el.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName))) return;
      const pos = nudge(tb, e.key, e.shiftKey, room);
      if (!pos) return;
      e.preventDefault();
      if (pos.x !== tb.x || pos.y !== tb.y) patch(tb.key, pos);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Azioni che farebbero perdere le modifiche: prima si chiede conferma
  const guard = (run) => (dirty ? setPending(() => run) : run());
  const pick = (id) => {
    if (id === roomId) return;
    guard(() => {
      const r = rooms.find((x) => x.id === id);
      setRoomId(id);
      setTables(r.tables.map(withKey));
      select(null);
      setDirty(false);
      setRoomForm(null);
      setAskDelete(false);
      setPending(null);
    });
  };

  const submitRoom = async () => {
    const name = (roomForm?.name || "").trim();
    if (!name || busy) return;
    if (roomForm.mode === "rename") {
      setBusy(true);
      try {
        const r = await SaveRoomAPI(room.id, { name });
        setRooms((list) => list.map((x) => (x.id === room.id ? { ...x, name: r?.name || name } : x)));
        setRoomForm(null);
      } catch (e) {
        ErrorToaster(errText(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    guard(async () => {
      setPending(null);
      setBusy(true);
      try {
        const r = await AddRoomAPI({ name });
        setRoomForm(null);
        await load(r.id);
      } catch (e) {
        ErrorToaster(errText(e));
      } finally {
        setBusy(false);
      }
    });
  };
  const deleteRoom = async () => {
    setBusy(true);
    try {
      await DeleteRoomAPI(room.id);
      setAskDelete(false);
      await load();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const addTable = () => {
    const nt = withKey(newTable(tables, allNames, room));
    update((list) => [...list, nt]);
    select(nt.key);
  };
  const duplicate = () => {
    if (!tb) return;
    const copy = withKey(duplicateTable(tb, tables, allNames, room));
    update((list) => [...list, copy]);
    select(copy.key);
  };
  const removeTable = () => {
    update((list) => list.filter((x) => x.key !== sel));
    select(null);
  };
  const save = async () => {
    setBusy(true);
    try {
      await SaveLayoutAPI(roomId, tables.map(strip));
      successToaster(t("plan.saved"));
      await load(roomId);
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  // Nome: obbligatorio e unico; se non valido resta il nome precedente
  const otherNames = [...otherRoomNames, ...tables.filter((x) => x.key !== sel).map((x) => x.name)];
  const nameErr = nameDraft !== null ? nameError(nameDraft, otherNames) : null;
  const onName = (v) => {
    setNameDraft(v);
    if (!nameError(v, otherNames)) patch(sel, { name: v.trim().slice(0, 30) });
  };

  const sum = roomSummary(tables);
  const allSeats = rooms
    .filter((r) => r.id !== roomId)
    .reduce((s, r) => s + roomSummary(r.tables).seatsMax, 0) + sum.seatsMax;
  const kinds = coversByKind(shifts);
  const kindText = Object.entries(kinds)
    .sort(([a], [b]) => (a === "Pranzo" ? -1 : b === "Pranzo" ? 1 : a.localeCompare(b)))
    .map(([k, n]) => `${t(`plan.kind.${k}`, { defaultValue: k })} ${n}`)
    .join(", ");

  const roomField = roomForm && (
    <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 6 }}>
      <Input
        autoFocus
        bsSize="sm"
        style={{ width: 240 }}
        maxLength={60}
        placeholder={t("plan.roomNamePlaceholder")}
        value={roomForm.name}
        onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === "Enter") submitRoom();
          if (e.key === "Escape") setRoomForm(null);
        }}
      />
      <Button size="sm" color="primary" className="m-0" disabled={!roomForm.name.trim() || busy} onClick={submitRoom}>
        {roomForm.mode === "new" ? t("plan.createRoom") : t("plan.renameConfirm")}
      </Button>
      <Button size="sm" color="link" className="m-0" onClick={() => setRoomForm(null)}>{t("plan.cancel")}</Button>
    </div>
  );

  return (
    <div ref={root}>
      <h3 className="mb-3">{t("plan.title")}</h3>

      <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 6 }}>
        {rooms.map((r) => (
          <Button key={r.id} size="sm" className="m-0" color={r.id === roomId ? "primary" : "secondary"} outline={r.id !== roomId} onClick={() => pick(r.id)}>
            {r.name}
          </Button>
        ))}
        <Button size="sm" color="link" className="m-0" onClick={() => { setAskDelete(false); setRoomForm({ mode: "new", name: "" }); }}>
          {t("plan.newRoom")}
        </Button>
      </div>
      {roomForm?.mode === "new" && roomField}

      {pending && (
        <div className="d-flex flex-wrap align-items-center mb-2 p-2" style={{ gap: 6, border: `1px solid ${RED}`, borderRadius: 4, color: RED }}>
          <span>{t("plan.discardConfirm", { name: room?.name || "" })}</span>
          <Button size="sm" color="danger" className="m-0" onClick={() => { const run = pending; setPending(null); setDirty(false); run(); }}>
            {t("plan.discard")}
          </Button>
          <Button size="sm" color="link" className="m-0" onClick={() => setPending(null)}>{t("plan.cancel")}</Button>
        </div>
      )}

      {loaded && !room && <p className="text-muted">{t("plan.createFirst")}</p>}
      {room && (
        <>
          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 6 }}>
            <Button size="sm" color="secondary" className="m-0" onClick={addTable}>{t("plan.addTable")}</Button>
            <Button size="sm" color="primary" className="m-0" disabled={!dirty || busy} onClick={save}>{t("plan.save")}</Button>
            <Button size="sm" color="link" className="m-0" onClick={() => { setAskDelete(false); setRoomForm({ mode: "rename", name: room.name }); }}>
              {t("plan.renameRoom")}
            </Button>
            <Button
              size="sm"
              color="link"
              className="m-0 text-danger"
              disabled={rooms.length < 2}
              title={rooms.length < 2 ? t("plan.deleteRoomOnlyOne") : undefined}
              onClick={() => { setRoomForm(null); setAskDelete(true); }}
            >
              {t("plan.deleteRoom")}
            </Button>
            {dirty && <strong style={{ color: RED }} className="small">{t("plan.unsaved")}</strong>}
          </div>
          {roomForm?.mode === "rename" && roomField}
          {askDelete && (
            <div className="d-flex flex-wrap align-items-center mb-2 p-2" style={{ gap: 6, border: `1px solid ${RED}`, borderRadius: 4, color: RED }}>
              <span>{t("plan.deleteRoomConfirm", { name: room.name, count: room.tables.length })}</span>
              <Button size="sm" color="danger" className="m-0" disabled={busy} onClick={deleteRoom}>{t("plan.deleteRoom")}</Button>
              <Button size="sm" color="link" className="m-0" onClick={() => setAskDelete(false)}>{t("plan.cancel")}</Button>
            </div>
          )}

          <Row>
            <Col xl="8" lg="7">
              <PlanCanvas
                room={room}
                tables={tables}
                selected={sel}
                editable
                onSelect={(x) => select(x.key)}
                onClear={() => select(null)}
                onMove={(x, nx, ny) => {
                  const pos = dragTo(x, nx, ny, room);
                  if (pos.x !== x.x || pos.y !== x.y) patch(x.key, pos);
                }}
              />
            </Col>
            <Col xl="4" lg="5" className="mt-3 mt-lg-0">
              {!tb && (
                <Card>
                  <CardBody>
                    <h5 className="mb-2">{room.name}</h5>
                    <div>{t("plan.tablesInUse", { inUse: sum.inUse, total: sum.total })}</div>
                    <div>{t("plan.seatsRange", { min: sum.seatsMin, max: sum.seatsMax })}</div>
                    <div className="small text-muted mt-2">
                      {t("plan.allRoomsSeats", { n: allSeats })} · {kindText ? t("plan.maxCovers", { list: kindText }) : t("plan.noShifts")}
                    </div>
                    <p className="small text-muted mt-2 mb-2">{t("plan.howTo")}</p>
                    {tables.length === 0 && <p className="text-muted mb-0">{t("plan.noTables")}</p>}
                    {tables.map((x) => (
                      <div
                        key={x.key}
                        role="button"
                        tabIndex={0}
                        onClick={() => select(x.key)}
                        onKeyDown={(e) => e.key === "Enter" && select(x.key)}
                        className="d-flex align-items-center py-1"
                        style={{ cursor: "pointer", gap: 8, opacity: x.active === false ? 0.55 : 1, borderTop: "1px solid rgba(128,128,128,.2)" }}
                      >
                        <span style={{ color: GOLD, width: 16, textAlign: "center" }}>{SHAPE_ICON[x.shape] || SHAPE_ICON.square}</span>
                        <strong>{x.name}</strong>
                        <span className="small text-muted ml-auto">
                          {t("plan.seats", { min: x.seats_min, max: x.seats_max })}
                          {x.active === false ? ` · ${t("plan.notInUse")}` : ""}
                        </span>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              )}
              {tb && (
                <Card key={tb.key}>
                  <CardBody>
                    <div className="d-flex align-items-center mb-2">
                      <h5 className="mb-0">{t("plan.tableTitle", { name: tb.name })}</h5>
                      <Button close className="ml-auto" aria-label={t("plan.close")} onClick={() => select(null)} />
                    </div>
                    <label className="mb-0 small">{t("plan.name")}</label>
                    <Input
                      bsSize="sm"
                      maxLength={30}
                      invalid={!!nameErr}
                      value={nameDraft ?? tb.name}
                      onChange={(e) => onName(e.target.value)}
                      onBlur={() => setNameDraft(null)}
                    />
                    {nameErr && <div className="small" style={{ color: RED }}>{t(nameErr)}</div>}
                    <Row className="mt-2">
                      <Col xs="6"><NumField label={t("plan.seatsMin")} field="seats_min" value={tb.seats_min} tb={tb} min={1} max={30} onPatch={(p) => patch(sel, p)} /></Col>
                      <Col xs="6"><NumField label={t("plan.seatsMax")} field="seats_max" value={tb.seats_max} tb={tb} min={1} max={30} onPatch={(p) => patchFit(sel, p)} /></Col>
                    </Row>
                    <label className="mt-2 mb-0 small">{t("plan.shape")}</label>
                    <Input type="select" bsSize="sm" value={tb.shape} onChange={(e) => patchFit(sel, shapePatch(tb, e.target.value))}>
                      <option value="square">{t("plan.shapeSquare")}</option>
                      <option value="rect">{t("plan.shapeRect")}</option>
                      <option value="round">{t("plan.shapeRound")}</option>
                    </Input>
                    <Row className="mt-2">
                      {tb.shape === "round" ? (
                        <Col xs="6"><NumField label={t("plan.diameter")} field="diameter" value={tb.width} tb={tb} min={40} max={400} step={10} onPatch={(p) => patchFit(sel, p)} /></Col>
                      ) : (
                        <>
                          <Col xs="4"><NumField label={t("plan.width")} field="width" value={tb.width} tb={tb} min={40} max={400} step={10} onPatch={(p) => patchFit(sel, p)} /></Col>
                          <Col xs="4"><NumField label={t("plan.height")} field="height" value={tb.height} tb={tb} min={40} max={400} step={10} onPatch={(p) => patchFit(sel, p)} /></Col>
                          <Col xs="4">
                            <label className="mb-0 small">{t("plan.rotation")}</label>
                            <Input type="select" bsSize="sm" value={ROTATIONS.includes(tb.rotation) ? tb.rotation : 0} onChange={(e) => patchFit(sel, { rotation: Number(e.target.value) })}>
                              {ROTATIONS.map((r) => <option key={r} value={r}>{r}°</option>)}
                            </Input>
                          </Col>
                        </>
                      )}
                    </Row>
                    <div className="mt-3">
                      <CustomInput type="switch" id={`joinable-${tb.key}`} label={t("plan.joinable")} checked={tb.joinable !== false} onChange={(e) => patch(sel, { joinable: e.target.checked })} />
                      <CustomInput type="switch" id={`active-${tb.key}`} label={t("plan.inUse")} checked={tb.active !== false} onChange={(e) => patch(sel, { active: e.target.checked })} />
                      {tb.active === false && <div className="small text-muted">{t("plan.inUseOffHint")}</div>}
                    </div>
                    <div className="d-flex flex-wrap mt-3" style={{ gap: 6 }}>
                      <Button size="sm" color="primary" outline className="m-0" onClick={duplicate}>{t("plan.duplicateTable")}</Button>
                      <Button size="sm" color="danger" className="m-0" onClick={removeTable}>{t("plan.deleteTable")}</Button>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default FloorEditor;
